import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Play, File } from 'lucide-react';
import '../styles/media.css';

interface Media {
  id: string;
  url: string;
  name?: string;
  type?: string;
  mimeType?: string;
}

interface MediaDisplayProps {
  media: Media[];
  onClick?: (index: number) => void;
  className?: string;
}

const MediaDisplay: React.FC<MediaDisplayProps> = ({ 
  media, 
  onClick,
  className = ''
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const handlePrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex(prev => (prev > 0 ? prev - 1 : prev));
  };

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex(prev => (prev < media.length - 1 ? prev + 1 : prev));
  };

  useEffect(() => {
    if (containerRef.current) {
      const scrollAmount = currentIndex * containerRef.current.offsetWidth;
      containerRef.current.scrollTo({ left: scrollAmount, behavior: 'smooth' });
    }
  }, [currentIndex]);

  const getMediaType = (item: Media): 'image' | 'video' | 'document' => {
    if (item.type === 'image' || (item.mimeType && item.mimeType.startsWith('image/'))) {
      return 'image';
    } else if (item.type === 'video' || (item.mimeType && item.mimeType.startsWith('video/'))) {
      return 'video';
    } else {
      return 'document';
    }
  };

  return (
    <div className={`media-carousel-container relative ${className}`}>
      <div 
        ref={containerRef}
        className="media-carousel-track flex overflow-x-hidden snap-x snap-mandatory"
      >
        {media.map((item, index) => {
          const mediaType = getMediaType(item);
          
          return (
            <div 
              key={item.id || index}
              className="media-carousel-slide min-w-full flex-shrink-0 snap-center"
              onClick={() => onClick && onClick(index)}
            >
              {mediaType === 'image' && (
                <img 
                  src={item.url} 
                  alt={item.name || `Media ${index + 1}`}
                  className="media-carousel-image object-contain w-full h-full cursor-pointer"
                />
              )}
              
              {mediaType === 'video' && (
                <div className="media-carousel-video-container relative w-full h-full flex items-center justify-center bg-black cursor-pointer">
                  <video 
                    src={item.url}
                    className="media-carousel-video max-h-full max-w-full"
                    controls={false}
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="bg-black bg-opacity-50 rounded-full p-3">
                      <Play className="h-8 w-8 text-white" />
                    </div>
                  </div>
                </div>
              )}
              
              {mediaType === 'document' && (
                <div className="media-carousel-document flex flex-col items-center justify-center h-full bg-gray-100 cursor-pointer p-4">
                  <File className="h-16 w-16 text-gray-500 mb-2" />
                  <p className="text-sm text-center text-gray-700 truncate max-w-full">
                    {item.name || 'Document'}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {media.length > 1 && (
        <>
          <button 
            onClick={handlePrev}
            disabled={currentIndex === 0}
            className={`media-carousel-nav-button left-2 ${currentIndex === 0 ? 'opacity-50 cursor-not-allowed' : 'opacity-80 hover:opacity-100'}`}
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          
          <button 
            onClick={handleNext}
            disabled={currentIndex === media.length - 1}
            className={`media-carousel-nav-button right-2 ${currentIndex === media.length - 1 ? 'opacity-50 cursor-not-allowed' : 'opacity-80 hover:opacity-100'}`}
          >
            <ChevronRight className="h-6 w-6" />
          </button>
          
          <div className="media-carousel-dots absolute bottom-2 left-0 right-0 flex justify-center gap-1.5">
            {media.map((_, index) => (
              <button
                key={index}
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentIndex(index);
                }}
                className={`h-1.5 rounded-full transition-all ${
                  currentIndex === index ? 'w-4 bg-white' : 'w-1.5 bg-white/60'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default MediaDisplay;
